// TTS line library
// Editing guide:
// - Keep sections in game-flow order so the script is easy to scan.
// - Variants live in STATUS_LINE_VARIANTS / DYNAMIC_TTS_VARIANTS.
// - Fallbacks live in TTS_FALLBACK_LINES / TTS_SINGLE_LINES.
// - Add new placeholders only if they are replaced in useTtsGameAnnouncements.
//
// Placeholder guide:
// {name} {username} {acronym} {bonusPoints} {bonusLabel} {votecount} {voteWord}
// {medalcolour} {category} {count} {roundWord} {player1} {player2}
// {answer1} {answer2} {round} {points} {leader} {trailer} {roundWinner}

// Single-use static lines (not variants).
export const TTS_SINGLE_LINES = {
  leaderboardTransitionIntro: "Lets see what that's done to the leaderboard!",
  leaderboardTransitionLeader: "Looks like we have a new leader! Let's move on to the next round.",
  leaderboardTransitionNoChange: "Okay nice. Let's move on to the next round.",
  acroversusFinalistsLocked: 'Looks like we have our finalists!'
};

export const buildCategoryChooserPrompt = ({
  isSelf = false,
  chooserName = '',
  isWinnerCreates = false,
  isFirstWinnerCreatesPrompt = false,
} = {}) => {
  const cleanChooserName = String(chooserName || '').trim() || 'The winner';
  if (isSelf) {
    if (isWinnerCreates && isFirstWinnerCreatesPrompt) {
      return "It's couch rules, that means you can write whatever category you like!";
    }
    return isWinnerCreates
      ? "It's your pick! Create a category for the next round."
      : "It's your pick! Select a category for the next round.";
  }
  return isWinnerCreates
    ? `${cleanChooserName} is creating a category for the next round.`
    : `${cleanChooserName} is selecting a category for the next round.`;
};

// --- Game Flow Variants ---
export const STATUS_LINE_VARIANTS = {
  // Lobby / welcome
  welcomeReturning: [
    'Hi there! Welcome back to Acroverse!',
    'Hello again! Back for more?',
    'Great to see you back!',
    'We meet again!',
    'Welcome back to Acroverse. Good to see you again!'
  ],
  welcomeHostFirstTime: [
    "Hi, I'm {hostName}. Welcome to Acroverse! Thanks for starting a game today! Take a look at the settings, and when you're ready, hit that Start button!",
    "Hi... {hostName} here. Welcome to Acroverse! Invite some friends, and when you're ready, hit start and I'll guide everyone through the game."
  ],
  welcomeFirstTime: [
    "Hey! I'm {hostName} - your host! Say \"hi\" to your fellow competitors in the chat, and I'll see you in the Acro-Verse!",
    "Hey there! I'm {hostName}, your host. Say hello in chat while we get ready to start the game."
  ],

  // Round start / category
  firstRoundIntro: [
    "Welcome—to the Acro-Verse. We're in for a great game today! — Okay, let's get started! The category for the first round is...",
    "We are now entering—the Acro-Verse. I hope you're all feeling inspired! — Right, let's begin! The first round category is..."
  ],
  begin: [
    'Begin!',
    "Let's Go!"
  ],

  // Answering / voting
  votingPrompt: [
    'Please cast your votes... now!',
    "Time to vote! Make your pick... now!"
  ],
  halfTime: [
    "That's half your time gone!",
    'Halfway there—keep it going!'
  ],
  lastPlayer: [
    "You're the last player left to submit!",
    'We are waiting on one last answer!'
  ],
  timesUp: [
      "Time's up!"
  ],

  // Results / scoring
  resultsIntro: [
    "Let's see what we have here.",
    'The results are in.'
  ],
  nonVoters: [
    "These players didn't vote, and don't score this round... Oops!",
    "No votes from these players, so no points this round."
  ],
  lookAtVotes: [
    "Let's take a look at the votes!",
    "Let's see how you all voted!"
  ],

  // Leaderboard
  leaderboardIntro: [
    "Let's see what that's done to the leaderboard!",
    "Let's check what that did to the leaderboard!",
    "Let's find out how that's affected the leaderboard!"
  ],
  leaderboardNewLeader: [
    "Looks like we have a new leader! Let's move on to the next round.",
    "Things are getting interesting at the top! Time for the next round.",
    'We have a new leader! Onward to the next round.'
  ],
  leaderboardTopTwoChange: [
    "Shake-up in the top two! Let's move on to the next round.",
    'The top two just changed—onward to the next round.',
    'New face in the top two! Time for the next round.'
  ],
  leaderboardNoChange: [
    "Okay nice. Let's move on to the next round.",
    "As we were then! Time for the next round.",
    'All right, moving on to the next round then.'
  ],

  // Finale / AcroVersus
  topTwoDecided: [
    'Our top 2 players have been decided',
    'We have our top two players!'
  ],
  acroVersusPrompt: [
    'Everyone else, get ready to vote! Good luck!',
    'Everybody else, get ready to cast your votes. Good luck!'
  ],
  acroVersusFinalistsWriting: [
    'The finalists are answering their first acronyms. Get ready to vote.',
    'Finalists are writing their first acronyms. Voters, get ready.',
    "Finalists are answering round one. Everyone else, get ready to vote."
  ],
  acroVersusWaitingForVoters: [
    "We're waiting for the other players to finish voting. Please wait for the results.",
    "Hang tight—waiting on the other players to finish voting.",
    "Please wait while the other players finish voting."
  ],

  // Game end
  gameEnd: [
    "And that's it for now. Thank you all for playing, and see you soon for another game of... Acroverse!",
    'Thanks for playing! See you soon for another game of Acroverse!'
  ]
};

// Variants for lines that include dynamic values (player names, acronyms, etc.)
// These are intentionally not added to static prewarm flows because they need runtime values.
export const DYNAMIC_TTS_VARIANTS = {
  // Results / winner
  winnerAnnouncement: [
    '{name} wins the round for "{acronym}".',
    '{name} is the winner this round with "{acronym}".',
    '{name} grabs the win for "{acronym}".'
  ],
  winnerWithBonus: [
    '{name} wins the round and receives {bonusPoints} bonus {bonusLabel}, for "{acronym}"!',
    '{name} is the winner this round and gets {bonusPoints} bonus {bonusLabel} with "{acronym}".',
    '{name} claims the win and earns {bonusPoints} bonus {bonusLabel} for "{acronym}".'
  ],
  winnerPraise: [
    'Nice one.',
    'Brilliant.',
    'Great work.',
    'Genius!',
    'Awesome.',
    'I love that!'
  ],

  // Host favourite - REMOVED to save TTS quota and speed up game
  // (kept in code history if needed later)

  // Tie-breaker explanations
  tieBreakByMedals: [
    'When players tie on points, we look at who has the most medals... Gold, then Silver, then Bronze.',
    'Tied scores? The medals decide it! Gold beats Silver beats Bronze.',
    "If there's a tie on points, we count the medals to break it."
  ],
  tieBreakByJoinTime: [
    'Still tied after medals? The player who joined first takes it!',
    'When medals are equal, the player who joined the game first wins the tie.',
    "Medals are tied? The earlier joiner gets the advantage!"
  ],
  tieBreakByMainGameScore: [
    "It's a draw, but with a higher score in the main game, {player} wins!",
    "Acro-Versus ends level — with the higher main-game score, {player} wins!",
    "It's a tie in Acro-Versus. The higher main-game score goes to {player}!"
  ],

  // Category / setup
  categoryReveal: [
    'The category is: {category}.',
    'Up next, the category is: {category}.',
    'This round, your category is: {category}.',
    "The new category for this round is: {category}."
  ],
  potLuckReveal: [
    "It's time to 'Read the Room'! Your category is: {category}.",
    "It's 'Read the Room!' time! Your category is: {category}.",
    "You're about to 'Read the Room!'. Your category is: {category}.",
    "It's a 'Read the Room!' round! Your category is: {category}.",
    "Get ready to 'Read the Room!'. Your category is: {category}.",
    "Are you ready to 'Read the Room!'? Your category is: {category}."
  ],
  potLuckIntro: [
    "It's time to 'Read the Room'! The category is...",
    "It's 'Read the Room!' time. The category is...",
    "You're about to 'Read the Room!'. The category is...",
    "It's a 'Read the Room!' round. The category is...",
    "Get ready to 'Read the Room!'. The category is...",
    "Are you ready to 'Read the Room!'? The category is..."
  ],
  finaleSetupRounds: [
    "After {count} {roundWord}, the top two players will go head to head in the acroversus finale. Let's see who can hold on to that top spot!",
    'In {count} {roundWord}, the top two players will go head to head in the acroversus finale. Its all to play for!',
    'Once we finish {count} {roundWord}, the top two players will go head to head in the acroversus finale. It can all change from here!'
  ],
  finaleSetupScore: [
    'Once a player hits {count} points, the top two players will go head to head in the acroversus finale.',
    'When someone reaches {count} points, the top two players will go head to head in the acroversus finale.',
    'The moment a player hits {count} points, the top two players will go head to head in the acroversus finale.'
  ],
  acroVersusSetup: [
    '{player1} and {player2} will now fight it out in the Acro-Versus quickfire round, to decide the overall winner.',
    "It's {player1} versus {player2} in the Acro-Versus quickfire showdown! Who's going to take the prize?",
    '{player1} and {player2} head to a quickfire Acro-Versus finale. The scores are reset - its anyones game now!'
  ],
  acroVersusCommentaryNoVotes: [
    'No votes that round. Nothing changes.',
    'No movement there. Nobody picked either side.',
    'No votes in that round, so the scores stay exactly where they are.'
  ],
  acroVersusCommentaryNeckAndNeck: [
    'It is neck and neck after round {round}.',
    'They are tied after round {round}. This is properly tense.',
    'All square after round {round}. Still anyones game.',
    'Nothing between them after round {round}.'
  ],
  acroVersusCommentaryClose: [
    'It is still anyones game.',
    'This is close. One good round could change everything.',
    'There is barely anything in it.',
    'Neither finalist has broken away yet.',
    'This is still right on a knife edge.'
  ],
  acroVersusCommentaryCloseOneToGo: [
    'There are only {points} points in it with a round to go.',
    'Just {points} points separate them, with one round left.',
    'One round to go, and only {points} points between them.',
    'This is going down to the wire. {points} points in it.'
  ],
  acroVersusCommentaryRunaway: [
    '{leader} is running away with it.',
    '{leader} is starting to pull clear.',
    '{leader} has opened up a serious lead now.',
    '{leader} is taking control of this Acro-Versus.',
    '{leader} is building a very useful cushion.'
  ],
  acroVersusCommentaryClawback: [
    '{roundWinner} clawed back some points that round.',
    '{roundWinner} needed that, and got it.',
    '{roundWinner} pulls it back a little.',
    '{roundWinner} is not letting this get away.',
    '{roundWinner} cuts the gap. We are not done yet.'
  ],
  acroVersusCommentaryHardToCatch: [
    'I cannot see {trailer} catching up from here.',
    '{trailer} needs something huge now.',
    'This is a mountain to climb for {trailer}.',
    '{leader} is almost out of sight now.',
    '{trailer} is running out of road.'
  ],
  acroVersusCommentaryBigRound: [
    'Big round for {roundWinner}.',
    '{roundWinner} made that one count.',
    'That was a strong round for {roundWinner}.',
    '{roundWinner} lands a useful hit there.',
    'That round goes heavily to {roundWinner}.'
  ],
  acroVersusCommentaryLeaderHolds: [
    '{leader} keeps the lead.',
    '{leader} is still in front.',
    '{leader} holds on at the top.',
    '{leader} protects the advantage.',
    'Still advantage {leader}.'
  ],
  acroVersusCommentaryRoundEdge: [
    '{roundWinner} edges that round.',
    'That round goes to {roundWinner}.',
    '{roundWinner} takes that one.',
    '{roundWinner} gets the better of that exchange.',
    'Small gain for {roundWinner}.'
  ],
  finalWinner: [
    'And the Acro-Versus winner is... {name}. Congratulations!',
    'Our Acro-Versus champion is... {name}! Congratulations!',
    'Give it up for {name}—you win the Acro-Versus!' 
  ],
  crownChallenge: [
    "Let's play another game. Can anyone take {name}'s crown? Good luck!",
    "The crown is on {name}. Can anyone take it? Good luck!",
    "Another game, another shot. Who can knock {name} off the throne?"
  ]
};

// Fallback lines for dynamic or optional variants used by the announcement hook.
export const TTS_FALLBACK_LINES = {
  // Round start / category
  categoryReveal: 'The category is: {category}.',
  begin: 'Begin!',

  // Results / scoring
  resultsIntro: 'Interesting!',
  winnerPraise: 'Nice one.',
  winnerWithBonus: '{name} wins the round and receives—{bonusPoints}—bonus {bonusLabel}, for—"{acronym}"!',
  winnerAnnouncement: '{name} wins the round for "{acronym}".',
  timesUp: "Time's Up!",

  // Finale / AcroVersus
  acroVersusPrompt: 'Everyone else, get ready to vote! Good luck!',
  acroVersusSetup:
    '{player1} and {player2} will now fight it out in the Acro-Versus quickfire round, to decide the overall winner.',
  acroVersusFinalistsWriting: 'The finalists are answering their first acronyms. Get ready to vote.',
  acroVersusWaitingForVoters: "We're waiting for the other players to finish voting. Please wait for the results.",
  finalWinner: 'And the winner is... {name}. Congratulations!',
  crownChallenge: "Let's play another game. Can anyone take {name}'s crown? Good luck!"
};

// Score reaction lines (used when awarding points)
export const SCORE_REACTION_LINES = {
  zero: [
    'Ah, unlucky!',
    'Unlucky!',
    "Don't worry, there's always next round!",
    "No points I'm afraid.",
    "Oof, that's harsh!"
  ],
  points: [
    'Nice, nice!',
    'Not bad!',
    'You can be proud of that!',
    'Yeah, good!',
    'Okay, fab!',
    "Hey, it's something!"
  ],
  bronze: [
    'Third place, nice!',
    "That's a bronze!",
    'Top three!',
    'Great work!',
    'Nice one, third!'
  ],
  silver: [
    'Woah, second place!',
    'So close, but still a great score!',
    "That's the silver, awesome!",
    'Silver, fantastic!'
  ],
  silverVeteran: [
    'Always the bridesmaid...'
  ],
  gold: [
    'You did it, amazing!',
    "Congrats, that's gold!",
    'Number one!',
    'Gonna be gonna be golden!',
    'Winner winner!',
    "Great job, that's the win!",
    'Look at all those votes!'
  ],
  goldRepeat: [
    "You've done it again!",
    'Again! Brilliant!',
    'You win again!',
    "That's another gold!"
  ],
  goldVeteran: [
    'Let someone else win for a change, haha! Great job!'
  ]
};
